/**
 * Tiny dependency-free SVG charts for the partner dashboard: a KPI sparkline,
 * a filled area/line trend chart, and a horizontal conversion funnel. They use
 * the app's gold-on-dark design tokens and scale to their container width.
 */
import { useId } from 'react';

/** Inline sparkline for KPI cards. Renders nothing useful for <2 points. */
export function Sparkline({
  values,
  width = 96,
  height = 28,
  color = 'var(--gold-primary)',
}: {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
}) {
  const gid = useId();
  if (values.length < 2) return null;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const stepX = width / (values.length - 1);
  const y = (v: number) => height - 2 - ((v - min) / span) * (height - 4);
  const pts = values.map((v, i) => `${(i * stepX).toFixed(1)},${y(v).toFixed(1)}`);
  const line = `M${pts.join(' L')}`;
  const area = `${line} L${width},${height} L0,${height} Z`;
  return (
    <svg
      className="pl-spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

export interface TrendPoint {
  date: string;
  value: number;
}

/** Full-width area/line trend with light gridlines and an accessible label.
 * When `previous` is supplied it is drawn as a faint dashed line on the same
 * scale — the equal-length window immediately before this one. */
export function AreaChart({
  points,
  previous,
  height = 180,
  color = 'var(--gold-primary)',
  format = (v: number) => String(v),
  label,
}: {
  points: TrendPoint[];
  previous?: number[];
  height?: number;
  color?: string;
  format?: (v: number) => string;
  label?: string;
}) {
  const gid = useId();
  const width = 640; // viewBox units; SVG scales to container via width:100%
  const padL = 8;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  if (points.length === 0) return null;
  const prevVals = previous ?? [];
  const max = Math.max(...points.map((p) => p.value), ...prevVals, 1);
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;
  const x = (i: number) => padL + i * stepX;
  const y = (v: number) => padT + innerH - (v / max) * innerH;
  const coords = points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`);
  const line = `M${coords.join(' L')}`;
  const area = `${line} L${x(points.length - 1).toFixed(1)},${padT + innerH} L${padL},${padT + innerH} Z`;
  const prevLine =
    prevVals.length > 1
      ? `M${prevVals.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' L')}`
      : '';
  const gridVals = [0, 0.5, 1];
  // Show at most ~6 date ticks to avoid crowding.
  const tickEvery = Math.max(1, Math.ceil(points.length / 6));
  return (
    <svg
      className="pl-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridVals.map((g) => (
        <g key={g}>
          <line
            x1={padL}
            x2={width - padR}
            y1={y(max * g)}
            y2={y(max * g)}
            stroke="var(--stroke)"
            strokeWidth="1"
          />
          <text x={padL} y={y(max * g) - 3} className="pl-chart__ytick">
            {format(Math.round(max * g))}
          </text>
        </g>
      ))}
      <path d={area} fill={`url(#${gid})`} />
      {prevLine && (
        <path
          d={prevLine}
          fill="none"
          stroke="var(--text-dim, #8a8a8a)"
          strokeWidth="1.5"
          strokeDasharray="4 3"
          strokeLinejoin="round"
          opacity="0.7"
        />
      )}
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      {points.map((p, i) =>
        i % tickEvery === 0 || i === points.length - 1 ? (
          <text key={p.date} x={x(i)} y={height - 6} className="pl-chart__xtick">
            {p.date.slice(5)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export interface FunnelStep {
  label: string;
  value: number;
}

/** Horizontal funnel: each step's bar width is relative to the top of funnel,
 * with the step-to-step conversion rate shown between rows. */
export function Funnel({ steps }: { steps: FunnelStep[] }) {
  const top = Math.max(steps[0]?.value ?? 0, 1);
  // Index of the step with the worst step-to-step conversion (biggest drop-off),
  // considered only where the upstream step actually had traffic.
  let worst = -1;
  let worstCr = Infinity;
  for (let i = 1; i < steps.length; i += 1) {
    const prev = steps[i - 1].value;
    if (prev > 0) {
      const cr = steps[i].value / prev;
      if (cr < worstCr) {
        worstCr = cr;
        worst = i;
      }
    }
  }
  return (
    <div className="pl-funnel">
      {steps.map((s, i) => {
        const pct = (s.value / top) * 100;
        const prev = i > 0 ? steps[i - 1].value : null;
        const cr = prev && prev > 0 ? (s.value / prev) * 100 : null;
        const isDrop = i === worst && steps.length > 2;
        return (
          <div key={s.label} className={`pl-funnel__row${isDrop ? ' is-drop' : ''}`}>
            <div className="pl-funnel__meta">
              <span className="pl-funnel__label">{s.label}</span>
              <span className="pl-funnel__value">{s.value.toLocaleString()}</span>
            </div>
            <div className="pl-funnel__track">
              <div className="pl-funnel__bar" style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
            {cr != null && (
              <span className="pl-funnel__cr" title="Conversion from previous step">
                {cr.toFixed(cr >= 10 ? 0 : 1)}%
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
